import React from 'react';
import {Link} from 'react-router-dom';
import {
    ArrowRightIcon, CalendarIcon, CloudIcon, CloudRainIcon, HeartIcon, PuzzleIcon,
    RefreshIcon, SparklesIcon, StoreIcon, SunIcon,
} from '../icons';
import {LandingCta} from './cta';
import {
    AI_BLOCK, DYNAMICS_BLOCK, ECOSYSTEM_BLOCK, INSIGHT_BLOCK, RATING_BLOCK,
    SHOPPING_BLOCK, STATS_BLOCK, WEATHER_BLOCK,
} from './content';
import RevealGroup from '../motion/RevealGroup';
import {DonutChart, DonutLegend, GarmentTile, RadarChart, SparkLine} from './visuals';

/**
 * Общий контейнер бенто-плитки.
 *
 * Карточки здесь уместны и осмысленны: это витрина разнородных данных, а не
 * способ разложить абзацы по коробкам. Разные размеры плиток задают ритм —
 * шесть одинаковых третей читались бы как таблица.
 */
const BentoCard: React.FC<{children: React.ReactNode; className?: string}> = ({children, className = ''}) => (
    <div className={`rounded-3xl border border-ink-100 dark:border-white/5 bg-white dark:bg-ink-900 shadow-card p-6 min-w-0 ${className}`}>
        {children}
    </div>
);

/** Заголовок плитки. Не кикер над заголовком — сам заголовок. */
const TileTitle: React.FC<{children: React.ReactNode; className?: string}> = ({children, className = ''}) => (
    <h3 className={`text-base font-semibold text-ink-900 dark:text-ink-50 ${className}`}>{children}</h3>
);

const WEATHER_ICONS = {
    sun: SunIcon,
    cloud: CloudIcon,
    rain: CloudRainIcon,
};

const ECOSYSTEM_ICONS: Record<string, React.FC<{className?: string}>> = {
    ai: SparklesIcon,
    shops: StoreIcon,
    calendar: CalendarIcon,
    integrations: PuzzleIcon,
};

const AiCard: React.FC = () => (
    <BentoCard className="lg:col-span-2 flex flex-col">
        <h3 className="text-xl font-bold leading-snug tracking-[-0.02em] text-ink-900 dark:text-ink-50">
            {AI_BLOCK.titleLead}
            <br/>
            <span className="text-brand-600 dark:text-brand-400">{AI_BLOCK.titleAccent}</span>
        </h3>
        <p className="mt-3 text-sm leading-6 text-ink-500 dark:text-ink-400">{AI_BLOCK.text}</p>
        <ul className="mt-auto pt-5 flex flex-wrap gap-2">
            {AI_BLOCK.occasions.map(occasion => (
                <li key={occasion} className="chip">{occasion}</li>
            ))}
        </ul>
    </BentoCard>
);

const WeatherCard: React.FC = () => (
    <BentoCard className="lg:col-span-2">
        <TileTitle>{WEATHER_BLOCK.title}</TileTitle>
        <ul className="mt-4 grid grid-cols-5 gap-1.5">
            {WEATHER_BLOCK.days.map(day => {
                const Icon = WEATHER_ICONS[day.kind];
                return (
                    <li key={day.id} className="rounded-xl bg-ink-50 dark:bg-white/5 py-2.5 text-center min-w-0">
                        <div className="text-[10px] text-ink-500 dark:text-ink-400 truncate px-1">{day.label}</div>
                        <Icon className="w-4 h-4 mx-auto my-1.5 text-brand-500"/>
                        <div className="text-[11px] font-semibold text-ink-900 dark:text-ink-50 tabular-nums">{day.temp}</div>
                    </li>
                );
            })}
        </ul>

        <div className="mt-5 rounded-2xl border border-ink-100 dark:border-white/5 p-3">
            <div className="text-xs font-medium text-ink-700 dark:text-ink-200">{WEATHER_BLOCK.outfitTitle}</div>
            <ul className="mt-3 grid grid-cols-5 gap-2">
                {WEATHER_BLOCK.outfit.map(piece => (
                    <li key={piece.id} className="min-w-0">
                        <GarmentTile kind={piece.kind} className="aspect-square w-full"/>
                        <div className="mt-1 text-[10px] text-ink-500 dark:text-ink-400 text-center truncate">
                            {piece.label}
                        </div>
                    </li>
                ))}
            </ul>
        </div>

        <div className="mt-4 flex items-center justify-between gap-3 text-xs">
            <span className="text-ink-500 dark:text-ink-400">
                {WEATHER_BLOCK.matchLabel}{' '}
                <span className="font-semibold text-brand-600 dark:text-brand-400">{WEATHER_BLOCK.matchValue}</span>
            </span>
            <span className="text-ink-400 dark:text-ink-500">{WEATHER_BLOCK.explain}</span>
        </div>
    </BentoCard>
);

const RatingCard: React.FC = () => (
    <BentoCard className="lg:col-span-2 flex flex-col">
        <h3 className="text-lg font-bold leading-snug text-ink-900 dark:text-ink-50">
            {RATING_BLOCK.titleLead}
            <br/>
            {RATING_BLOCK.titleSecond}{' '}
            <span className="text-brand-600 dark:text-brand-400">{RATING_BLOCK.titleAccent}</span>
        </h3>
        <ul className="mt-5 grid grid-cols-4 gap-2">
            {RATING_BLOCK.looks.map(look => (
                <li key={look.id} className="relative min-w-0">
                    <GarmentTile kind={look.kind} className="aspect-[4/5] w-full"/>
                    <HeartIcon className="absolute top-1.5 right-1.5 w-3.5 h-3.5 text-brand-500" filled/>
                </li>
            ))}
        </ul>
        <div className="mt-auto pt-4 flex items-baseline gap-2">
            <span className="text-lg font-bold text-brand-600 dark:text-brand-400">{RATING_BLOCK.growth}</span>
            <span className="text-xs text-ink-500 dark:text-ink-400">{RATING_BLOCK.growthNote}</span>
        </div>
    </BentoCard>
);

const StatsCard: React.FC = () => (
    <BentoCard className="lg:col-span-3">
        <TileTitle>{STATS_BLOCK.title}</TileTitle>
        <div className="mt-5 flex items-center gap-5">
            <DonutChart segments={STATS_BLOCK.segments} centerValue={STATS_BLOCK.donutValue}/>
            <div className="min-w-0 flex-1">
                <DonutLegend segments={STATS_BLOCK.segments}/>
            </div>
        </div>
        <p className="mt-4 text-xs text-ink-500 dark:text-ink-400">{STATS_BLOCK.donutNote}</p>
        <div className="mt-4 pt-4 border-t border-ink-100 dark:border-white/5 flex items-baseline justify-between gap-3">
            <span className="text-xs text-ink-500 dark:text-ink-400">{STATS_BLOCK.valueLabel}</span>
            <span className="text-base font-bold text-ink-900 dark:text-ink-50">{STATS_BLOCK.valueAmount}</span>
        </div>
    </BentoCard>
);

/** Радар «сильные и слабые места гардероба» — соседствует со статистикой не случайно. */
const InsightCard: React.FC = () => (
    <BentoCard className="lg:col-span-3 flex flex-col">
        <h3 className="text-lg font-bold leading-snug tracking-[-0.02em] text-ink-900 dark:text-ink-50">
            {INSIGHT_BLOCK.titleLead}{' '}
            {INSIGHT_BLOCK.titleSecond}{' '}
            <span className="text-brand-600 dark:text-brand-400">{INSIGHT_BLOCK.titleAccent}</span>
        </h3>

        <div className="mt-5 flex flex-col sm:flex-row items-center gap-6">
            <RadarChart axes={INSIGHT_BLOCK.radar}/>
            <ul className="grid grid-cols-2 gap-2 w-full min-w-0">
                {INSIGHT_BLOCK.tiles.map(tile => (
                    <li key={tile.id} className="rounded-xl bg-ink-50 dark:bg-white/5 px-3 py-2 min-w-0">
                        <div className="text-lg font-bold text-ink-900 dark:text-ink-50 tabular-nums">{tile.value}</div>
                        <div className="text-[11px] leading-4 text-ink-500 dark:text-ink-400">{tile.label}</div>
                    </li>
                ))}
            </ul>
        </div>
    </BentoCard>
);

const EcosystemCard: React.FC = () => (
    <BentoCard className="lg:col-span-4">
        <h3 className="text-lg font-bold leading-snug text-ink-900 dark:text-ink-50">
            {ECOSYSTEM_BLOCK.titleLead}
            <br/>
            <span className="text-brand-600 dark:text-brand-400">{ECOSYSTEM_BLOCK.titleAccent}</span>
        </h3>
        <p className="mt-3 text-sm leading-6 text-ink-500 dark:text-ink-400">{ECOSYSTEM_BLOCK.text}</p>
        <ul className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {ECOSYSTEM_BLOCK.tiles.map(tile => {
                const Icon = ECOSYSTEM_ICONS[tile.id];
                return (
                    <li key={tile.id} className="rounded-2xl bg-ink-50 dark:bg-white/5 p-3 text-center min-w-0">
                        <Icon className="w-5 h-5 mx-auto text-brand-600 dark:text-brand-400"/>
                        <div className="mt-2 text-[11px] leading-4 text-ink-600 dark:text-ink-300">{tile.title}</div>
                    </li>
                );
            })}
        </ul>
        <div className="mt-4 flex items-center gap-2 text-xs text-ink-500 dark:text-ink-400">
            <RefreshIcon className="w-4 h-4 text-brand-500"/>
            {ECOSYSTEM_BLOCK.note}
        </div>
    </BentoCard>
);

const ShoppingCard: React.FC<{cta: LandingCta}> = ({cta}) => (
    <BentoCard className="lg:col-span-2 flex flex-col">
        <TileTitle>{SHOPPING_BLOCK.title}</TileTitle>
        <ul className="mt-4 grid grid-cols-4 gap-2">
            {SHOPPING_BLOCK.items.map(item => (
                <li key={item.id} className="min-w-0">
                    <GarmentTile kind={item.kind} className="aspect-[4/5] w-full"/>
                </li>
            ))}
        </ul>
        <p className="mt-5 text-sm leading-6 text-ink-500 dark:text-ink-400">{SHOPPING_BLOCK.text}</p>
        <Link to={cta.primary.href} className="btn-secondary h-10 mt-auto pt-0 self-start">
            {SHOPPING_BLOCK.cta}
            <ArrowRightIcon className="w-4 h-4"/>
        </Link>
    </BentoCard>
);

/**
 * Замыкающая полоса бенто: одна метрика во всю ширину. После плотной сетки
 * плиток она читается как выдох — и как обещание, что динамика видна сразу.
 */
const DynamicsCard: React.FC = () => (
    <BentoCard className="lg:col-span-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-6">
            <div className="min-w-0 sm:w-64 shrink-0">
                <TileTitle className="!text-sm !font-medium !text-ink-600 dark:!text-ink-300">
                    {DYNAMICS_BLOCK.title}
                </TileTitle>
                <div className="mt-2 flex items-baseline gap-2">
                    <span className="text-4xl font-bold tracking-[-0.03em] text-ink-900 dark:text-ink-50 tabular-nums">
                        {DYNAMICS_BLOCK.value}
                    </span>
                    <span className="text-xs text-ink-500 dark:text-ink-400">{DYNAMICS_BLOCK.note}</span>
                </div>
            </div>
            <div className="min-w-0 flex-1">
                <SparkLine points={DYNAMICS_BLOCK.points}/>
                <ul className="mt-2 flex justify-between text-[10px] text-ink-400 dark:text-ink-500">
                    {DYNAMICS_BLOCK.months.map(month => (
                        <li key={month}>{month}</li>
                    ))}
                </ul>
            </div>
        </div>
    </BentoCard>
);

/**
 * Бенто «AI и аналитика»: рекомендации, погода, оценки, статистика, экосистема,
 * шопинг и динамика носки. Плитки разного размера — сетка на шесть колонок.
 */
const IntelligenceSections: React.FC<{cta: LandingCta}> = ({cta}) => (
    <section id="intelligence" className="scroll-mt-20 py-4 sm:py-8" aria-labelledby="landing-intelligence-title">
        <div className="max-w-[34ch]">
            <h2
                id="landing-intelligence-title"
                className="text-3xl sm:text-4xl font-bold leading-[1.06] tracking-[-0.03em] text-ink-900 dark:text-ink-50 text-balance"
            >
                AI работает с тем, что у вас уже есть
            </h2>
        </div>
        <p className="mt-5 text-base leading-7 text-ink-600 dark:text-ink-300 max-w-[52ch]">
            Рекомендации, погода, шопинг и статистика — в одном месте.{' '}
            <span className="text-ink-500 dark:text-ink-400">
                Числа и карточки ниже — пример витрины, а не реальные данные.
            </span>
        </p>

        <RevealGroup className="mt-10 grid gap-5 lg:grid-cols-6">
            <AiCard/>
            <WeatherCard/>
            <RatingCard/>
            <StatsCard/>
            <InsightCard/>
            <EcosystemCard/>
            <ShoppingCard cta={cta}/>
            <DynamicsCard/>
        </RevealGroup>
    </section>
);

export default IntelligenceSections;
